import usePermissions from "@/hooks/use-permissions.hook.ts";
import { BlogPost } from "@/pages/blog/components/BlogPost.tsx";
import { BlogPostSkeleton } from "@/pages/blog/components/BlogPostSkeleton.tsx";
import { BlogChannelSummary } from "@/pages/blog/index/components/BlogChannelSummary.tsx";
import { BlogChannelEditModal } from "@/pages/blog/index/widgets/BlogChannelEditModal.tsx";
import { useBlogMyChannels, useBlogPostsSearch } from "@/queries/blog.query.ts";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import { ApiBlogChannelCan } from "@/types/blog.types.ts";
import { AppEntityViewKey } from "@/types/common.types.ts";
import { ApiApplicantMentionState } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Avatar,
  Button,
  ButtonLink,
  EmptyStateBase,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export function BlogChannelPosts() {
  const { channel_id } = useParams();
  const { t } = useTranslation();
  const channelId = channel_id ? Number(channel_id) : undefined;
  const isMentionPage = new RegExp("/news/mentions", "gi").test(location.pathname);
  const { data: channelsForView, isLoading: isLoadingChannelsForView } = useBlogMyChannels();
  const { data: channelsForPublish, isLoading: isLoadingChannelsForPublishing } = useBlogMyChannels(
    {
      can: ApiBlogChannelCan.publish,
    }
  );
  const { isEntityViewed, setEntityViewed } = useEmployeeAppConfigStore();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const loaderAim = useRef<HTMLDivElement>(null);
  const {
    data: postPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBlogPostsSearch(
    {
      page_number: 1,
      channel_ids: channelId
        ? [channelId]
        : !isMentionPage
          ? channelsForView?.reduce((acc: number[], channel) => {
              if (channel.is_subscribed) acc.push(channel.id);
              return acc;
            }, [])
          : [],
      mention_state: isMentionPage ? ApiApplicantMentionState.any : undefined,
    },
    !isLoadingChannelsForView
  );
  const flatData = useMemo(
    () => postPages?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [postPages]
  );
  const subscribesIsEmpty = useMemo(
    () => channelsForView?.every((channel) => !channel.is_subscribed),
    [channelsForView]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          await fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "400px" }
    );

    if (loaderAim.current) {
      observer.observe(loaderAim.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const promoForAddPostNeeded = useMemo(() => {
    const canPostInChannel = channelId && channelsForPublish?.some((ch) => ch.id == channelId);

    return !isLoading && !isLoadingChannelsForPublishing && !flatData.length && canPostInChannel;
  }, [isLoadingChannelsForPublishing, isLoading, flatData, channelsForPublish]);

  const promoForAddChannelNeeded = useMemo(() => {
    return (
      !channelId &&
      !isLoading &&
      !flatData.length &&
      !isLoadingChannelsForView &&
      channelsForView &&
      !channelsForView.length
    );
  }, [isLoadingChannelsForView, isLoading, flatData, channelsForView]);

  const handleAddChannel = () => {
    openModal(BlogChannelEditModal);
  };

  return (
    <Stack className={"container-sm ml-auto mr-auto"} gap={"lg"}>
      {channelId ? (
        <BlogChannelSummary channelId={channelId} key={`channel-summary-${channelId}`} />
      ) : !isLoadingChannelsForView &&
        channelsForView &&
        channelsForView.length > 0 &&
        subscribesIsEmpty &&
        !isEntityViewed(AppEntityViewKey.blog_empty_subscribes) ? (
        <ListItem
          title={t("blog:create_yours_feed_title")}
          caption={t("blog:create_yours_feed_caption")}
          icon={<Avatar size={"lg"} icon={<FontAwesomeIcon icon="fa-light fa-bell" />} />}
          forceShowAvatar={true}
          behavior={"card"}
          valueSlot={
            <Button
              onClick={() => {
                setEntityViewed(AppEntityViewKey.blog_empty_subscribes);
              }}
            >
              {t("blog:got_it")}
            </Button>
          }
        />
      ) : (
        <></>
      )}
      {isLoading && !flatData.length && <BlogPostSkeleton />}
      {channelId ? (
        promoForAddPostNeeded && (
          <ListItem
            icon={<Avatar size={"lg"} icon={<FontAwesomeIcon icon="fa-light fa-plus" />} />}
            title={t("blog:no_posts")}
            caption={t("blog:channel_is_ready")}
            forceShowAvatar={true}
            behavior={"card"}
            valueSlot={<ButtonLink href={"/news/posts/new"}>{t("blog:new_post")}</ButtonLink>}
          />
        )
      ) : promoForAddChannelNeeded ? (
        canIEdit(ApiPermission.news_manage_channels) ? (
          <ListItem
            icon={<Avatar size={"lg"} icon={<FontAwesomeIcon icon="fa-light fa-hashtag" />} />}
            title={t("blog:create_first_channel")}
            caption={t("blog:bring_your_team_updates")}
            forceShowAvatar={true}
            behavior={"card"}
            valueSlot={
              <Button
                onClick={() => {
                  handleAddChannel();
                }}
              >
                {t("blog:create_channel")}
              </Button>
            }
          />
        ) : (
          <EmptyStateBase
            emoji={":eyes:"}
            title={t("blog:nothing_here")}
            description={t("blog:nothing_here_caption")}
          />
        )
      ) : (
        <></>
      )}
      {flatData.map((post) => (
        <BlogPost post={post} key={`post-${post.id}`} />
      ))}
      {isFetchingNextPage && <Skeleton className={"h-5 w-full"} />}
      <div ref={loaderAim}></div>
    </Stack>
  );
}
